// Pricing migration 32 (ADO-3985), D-2, T18 and T19: what a cart change does to the multi-buy
// deals on screen. Mounted once at the composition root; renders nothing.
//
// - D-2: once the shop's cart writes have all settled, the deals of the products they touched,
//   and of every product sharing a deal with them, are asked for again, so each deal's progress
//   is the server's after the change. The web never counts cart lines (D-2).
// - Only settled rows are compared (the story's in-flight note): the before picture is taken when
//   the first write of a run starts and the after picture once every write has settled and the
//   deals have answered again, so a row still in flight never feeds a comparison. Free units are
//   compared from the deal read's progress (`freeUnitsAtCurrent`, row 31's own arithmetic, so on
//   WOW it is the free units the cart lines carry), which also gives the cases still needed.
// - WOW (boards 08, 11): a deal gaining free cases shows "Multi-buy applied"; when its dialog is
//   open it waits for the dialog to close (08: "Closing shows the toast"). Losing free cases
//   (R3 21w) shows "Free case removed" with "Add n case" (the cases now short), which adds them
//   back to the line the shop reduced most, else the deal's first product, never to an
//   unavailable line (no action when every line is). With no free case left it names the tier
//   ("… short of Buy 4, get 1 free"); with some left, "… short of getting it back".
// - The deal read unavailable (AC6): when the re-read did not answer, or the deal left the
//   answer, nothing is compared from the deal read. On WOW a free case gone from the cart lines
//   (row 31's `freeUnitsEarned`) still shows "Free case removed", with no tier and no action; on a
//   standard entry nothing is said.
// - One toast per run: a loss wins over a gain, several losses show one toast (the strips carry
//   each deal's state, 21w), and a gain arriving while a loss toast is still on screen is
//   dropped, never shown over it.
// - Standard entries (R1 11s, R3 21s): reaching a deal shows "Multi-buy deal reached"; dropping
//   below it shows "Multi-buy deal no longer reached" with the same action.
// - A deal dropped below is remembered as lost until it gives free units again (the "… back" and
//   "… again" copy, 21w-b, 21s-b).
// Only the shop's own writes count: a basket read (a hydrate, an entry switch) changes nothing.

import { useEffect } from 'react';
import type { BasketLine } from '../api/basket.client';
import type { MultibuyDeal } from '../api/multibuy-deals.client';
import { useAvailabilityStore } from '../availability/useAvailabilityStore';
import { useBasketStore } from '../basket/useBasketStore';
import { isWowEntryKey } from '../basket/wowEntry';
import { useSessionStore } from '../auth/useSessionStore';
import { useProductDetailStore } from '../product-detail/useProductDetailStore';
import { useToastStore, type ToastMessage } from '../toast/useToastStore';
import { lowestTier, tierText } from './multibuyDealText';
import { useDealDialogStore } from './useDealDialogStore';
import { useDealSessionStore } from './useDealSessionStore';
import { useMultibuyDealsStore } from './useMultibuyDealsStore';

const cases = (count: number): string => `${count} ${count === 1 ? 'case' : 'cases'}`;

/** Every deal the store holds, by offer code (the latest answer read for each). */
function heldDeals(): Map<string, MultibuyDeal> {
  const deals = new Map<string, MultibuyDeal>();
  for (const entry of Object.values(useMultibuyDealsStore.getState().entries)) {
    if (entry.state !== 'ready') continue;
    for (const deal of entry.deals) deals.set(deal.multibuyOfferCode, deal);
  }
  return deals;
}

/** The deals of `skus`, by offer code, from those SKUs' own (re-read) entries. */
function dealsOf(skus: readonly string[]): Map<string, MultibuyDeal> {
  const { entries } = useMultibuyDealsStore.getState();
  const deals = new Map<string, MultibuyDeal>();
  for (const sku of skus) {
    const entry = entries[sku];
    if (entry?.state !== 'ready') continue;
    for (const deal of entry.deals) deals.set(deal.multibuyOfferCode, deal);
  }
  return deals;
}

/** The touched SKUs and every held SKU sharing a deal with one of them. */
function skusToAskAgain(touched: ReadonlySet<string>): string[] {
  const skus = new Set(touched);
  for (const [sku, entry] of Object.entries(useMultibuyDealsStore.getState().entries)) {
    if (entry.state !== 'ready') continue;
    const sharesADeal = entry.deals.some((deal) =>
      deal.products.some((product) => touched.has(product.skuCode)),
    );
    if (sharesADeal) skus.add(sku);
  }
  return [...skus];
}

function freeProductName(deal: MultibuyDeal): string | null {
  const sku = deal.freeProduct?.skuCode;
  return sku === undefined
    ? null
    : (useProductDetailStore.getState().bySku[sku]?.product?.name ?? null);
}

/** The WOW free units the cart lines carry, by the deal that earned them (row 31). */
function freeUnitsByDeal(lines: readonly BasketLine[]): Map<string, number> {
  const units = new Map<string, number>();
  for (const line of lines) {
    const code = line.multibuyOfferCode;
    if (code === undefined || code === null || (line.freeUnitsEarned ?? 0) <= 0) continue;
    units.set(code, (units.get(code) ?? 0) + (line.freeUnitsEarned ?? 0));
  }
  return units;
}

const isUnavailable = (sku: string): boolean =>
  useAvailabilityStore.getState().entries[sku]?.status === 'UNAVAILABLE';

/** The deal's product the shop reduced most in this run, for "Add n case" to add back to, else
 *  its first product; never an unavailable one (R6), so `null` when every line is. */
function reducedLine(
  deal: MultibuyDeal,
  before: ReadonlyMap<string, number>,
  after: ReadonlyMap<string, number>,
): string | null {
  const available = deal.products.filter((product) => !isUnavailable(product.skuCode));
  let best: { sku: string; drop: number } | null = null;
  for (const { skuCode } of available) {
    const drop = (before.get(skuCode) ?? 0) - (after.get(skuCode) ?? 0);
    if (drop > 0 && (best === null || drop > best.drop)) best = { sku: skuCode, drop };
  }
  return best?.sku ?? available[0]?.skuCode ?? null;
}

function addCases(sku: string, count: number): void {
  const { quantityBySku, add, setQuantity } = useBasketStore.getState();
  const quantity = quantityBySku.get(sku) ?? 0;
  if (quantity === 0) void add(sku, count);
  else void setQuantity(sku, quantity + count);
}

function gainedToast(deal: MultibuyDeal, wow: boolean, gained: number): ToastMessage | null {
  if (!wow) {
    return {
      title: 'Multi-buy deal reached',
      description: 'BWG adds the free stock when it processes your order',
      inCartPanel: true,
    };
  }
  const name = freeProductName(deal);
  const description =
    gained === 1
      ? name === null
        ? 'Free case added'
        : `Free ${name} case added`
      : name === null
        ? `${gained} free cases added`
        : `${gained} free ${name} cases added`;
  return { title: 'Multi-buy applied', description, inCartPanel: true };
}

const freeCasesRemoved = (lost: number): string =>
  lost === 1 ? 'Free case removed' : `${lost} free cases removed`;

function lostToast(
  deal: MultibuyDeal,
  wow: boolean,
  lost: number,
  addBackTo: string | null,
): ToastMessage | null {
  if (deal.kind !== 'FREE_UNITS') return null;
  const short = deal.progress.unitsToNext ?? lowestTier(deal).buyQuantity;
  const toast: ToastMessage = {
    title: wow ? freeCasesRemoved(lost) : 'Multi-buy deal no longer reached',
    // With free cases still earned the tier is reached, so the cases short are to the one lost.
    description:
      deal.progress.freeUnitsAtCurrent > 0
        ? `You're ${cases(short)} short of getting it back.`
        : `You're ${cases(short)} short of ${tierText(lowestTier(deal))}.`,
    tone: 'warning',
    inCartPanel: true,
  };
  return addBackTo === null
    ? toast
    : {
        ...toast,
        action: { label: `Add ${cases(short)}`, onAction: () => addCases(addBackTo, short) },
      };
}

/** A gain toast held while its deal's dialog is open (08: "Closing shows the toast"), with the
 *  deal and the free units it was for, so it is dropped if the deal has since lost them. */
let heldGain: {
  readonly toast: ToastMessage;
  readonly code: string;
  readonly free: number;
} | null = null;
/** A WOW free case gone from the cart lines with the deal read unavailable (AC6): no tier and no
 *  action, as neither is known. */
function lostFromTheLinesToast(lost: number): ToastMessage {
  return { title: freeCasesRemoved(lost), description: '', tone: 'warning', inCartPanel: true };
}

/** Whether the loss toast the watcher showed is still the one on screen. */
function lossOnScreen(): boolean {
  return shownLoss !== null && useToastStore.getState().toast?.id === shownLoss.id;
}

/** The loss toast on screen and its deal, dismissed once the deal is reached again. */
let shownLoss: { readonly id: number; readonly code: string } | null = null;
/** Each deal as the last settled run read it: the before picture of a write that started while an
 *  earlier run was still settling, so it never compares against older answers. Used only then:
 *  otherwise the store's answer at the write's start is the fresher one (review cycle 2: deals
 *  are also read again outside a run, on an entry or account switch or by a page). */
let lastSettled = new Map<string, MultibuyDeal>();
/** Runs settle one after another, each against the last one's answers. */
let runs: Promise<void> = Promise.resolve();
/** Runs queued and not yet settled. */
let runsPending = 0;
/** Bumped by every reset: a run queued before it says and records nothing afterwards (review
 *  cycle 3: a sign-out while a run waits for its re-read). */
let generation = 0;

/** Note the loss toast on screen as `offerCode`'s (the watcher does this itself when it shows
 *  one; exported for the specs). */
export function noteLossToast(offerCode: string): void {
  const shown = useToastStore.getState().toast;
  shownLoss = shown === null ? null : { id: shown.id, code: offerCode };
}

/** Close the loss toast once its deal is opened (R3 21w-b: "The 21w toast is gone by now, or
 *  closes when the deal opens"): the deal itself now says how to get the free case back. */
export function dismissLossToast(): void {
  if (shownLoss === null) return;
  useToastStore.getState().dismissToast(shownLoss.id);
  shownLoss = null;
}

/** Forget the held and shown toasts and the settled picture (sign-out, unmount). */
export function resetMultibuyWatcher(): void {
  heldGain = null;
  shownLoss = null;
  lastSettled = new Map();
  runs = Promise.resolve();
  runsPending = 0;
  generation += 1;
}

interface RunToast {
  readonly message: ToastMessage;
  readonly code: string;
  readonly free: number;
}

async function settleRun(
  touched: ReadonlySet<string>,
  startedWith: ReadonlyMap<string, MultibuyDeal>,
  quantitiesBefore: ReadonlyMap<string, number>,
  freeUnitsBefore: ReadonlyMap<string, number>,
  wow: boolean,
  overlapped: boolean,
  queuedIn: number,
): Promise<void> {
  if (queuedIn !== generation) return;
  const before = new Map(startedWith);
  if (overlapped) {
    for (const [code, deal] of lastSettled) if (before.has(code)) before.set(code, deal);
  }
  const asked = skusToAskAgain(touched);
  const outcome = await useMultibuyDealsStore.getState().invalidate(asked);
  if (queuedIn !== generation) return;
  // A re-read that did not answer leaves older answers in the store: none is compared (AC6).
  const after = outcome === 'unread' ? new Map<string, MultibuyDeal>() : dealsOf(asked);
  for (const [code, deal] of after) lastSettled.set(code, deal);
  const { quantityBySku: quantitiesAfter, lines: linesAfter } = useBasketStore.getState();
  const freeUnitsAfter = freeUnitsByDeal(linesAfter);
  const session = useDealSessionStore.getState();
  const toasts = useToastStore.getState();
  const losses: RunToast[] = [];
  const gains: RunToast[] = [];
  const ordered = [...before].sort(([left], [right]) => (left < right ? -1 : 1));
  for (const [code, was] of ordered) {
    if (!was.products.some((product) => touched.has(product.skuCode))) continue;
    const now = after.get(code);
    if (now === undefined) {
      // The deal read cannot say; on WOW the cart lines still carry the free cases (row 31).
      const lost = (freeUnitsBefore.get(code) ?? 0) - (freeUnitsAfter.get(code) ?? 0);
      if (!wow || lost <= 0) continue;
      session.markLost(code);
      if (heldGain?.code === code) heldGain = null;
      losses.push({
        message: lostFromTheLinesToast(lost),
        code,
        free: freeUnitsAfter.get(code) ?? 0,
      });
      continue;
    }
    const free = now.progress.freeUnitsAtCurrent;
    const change = free - was.progress.freeUnitsAtCurrent;
    if (change > 0) {
      session.markRegained(code);
      // The shortfall the loss toast offered to make up is gone (review cycle 1).
      if (shownLoss?.code === code) {
        toasts.dismissToast(shownLoss.id);
        shownLoss = null;
      }
      const gained =
        wow || was.progress.freeUnitsAtCurrent === 0 ? gainedToast(now, wow, change) : null;
      if (gained !== null) gains.push({ message: gained, code, free });
    } else if (change < 0) {
      session.markLost(code);
      if (heldGain?.code === code) heldGain = null;
      if (wow || free === 0) {
        const lost = lostToast(
          now,
          wow,
          -change,
          reducedLine(now, quantitiesBefore, quantitiesAfter),
        );
        if (lost !== null) losses.push({ message: lost, code, free });
      }
    }
  }
  const toast = losses[0] ?? gains[0] ?? null;
  if (toast === null) return;
  if (toast.message.tone !== 'warning') {
    // A gain never replaces a loss toast still on screen.
    if (lossOnScreen()) return;
    if (useDealDialogStore.getState().target !== null) {
      heldGain = { toast: toast.message, code: toast.code, free: toast.free };
      return;
    }
  }
  toasts.showToast(toast.message);
  const shown = useToastStore.getState().toast;
  shownLoss =
    toast.message.tone === 'warning' && shown !== null ? { id: shown.id, code: toast.code } : null;
}

/** On the dialog closing, the held gain shows if its deal still has those free units. */
function showHeldGain(): void {
  const held = heldGain;
  heldGain = null;
  if (held === null || lossOnScreen()) return;
  const deal = lastSettled.get(held.code);
  if (deal !== undefined && deal.progress.freeUnitsAtCurrent < held.free) return;
  useToastStore.getState().showToast(held.toast);
  shownLoss = null;
}

export function MultibuyCartWatcher() {
  useEffect(() => {
    let touched = new Set<string>();
    let before: Map<string, MultibuyDeal> = new Map();
    let quantitiesBefore: ReadonlyMap<string, number> = new Map();
    let freeUnitsBefore: ReadonlyMap<string, number> = new Map();
    let overlapped = false;
    const unsubscribeBasket = useBasketStore.subscribe((state, previous) => {
      const started = [...state.pending].filter((sku) => !previous.pending.has(sku));
      if (started.length > 0) {
        if (touched.size === 0) {
          before = heldDeals();
          quantitiesBefore = previous.quantityBySku;
          freeUnitsBefore = freeUnitsByDeal(previous.lines);
          overlapped = runsPending > 0;
        }
        for (const sku of started) touched.add(sku);
      }
      if (state.pending.size === 0 && previous.pending.size > 0 && touched.size > 0) {
        const run = touched;
        const startedWith = before;
        const quantities = quantitiesBefore;
        const linesFreeUnits = freeUnitsBefore;
        const afterAnotherRun = overlapped;
        touched = new Set();
        const wow = isWowEntryKey(useSessionStore.getState().activeEntryKey);
        const queuedIn = generation;
        runsPending += 1;
        runs = runs
          .then(() =>
            settleRun(run, startedWith, quantities, linesFreeUnits, wow, afterAnotherRun, queuedIn),
          )
          .catch(() => undefined)
          .finally(() => {
            if (queuedIn === generation) runsPending = Math.max(0, runsPending - 1);
          });
      }
    });
    const unsubscribeDialog = useDealDialogStore.subscribe((state, previous) => {
      if (previous.target !== null && state.target === null) showHeldGain();
      if (previous.target === null && state.target !== null) dismissLossToast();
    });
    return () => {
      unsubscribeBasket();
      unsubscribeDialog();
      resetMultibuyWatcher();
    };
  }, []);
  return null;
}
